import { BaseComponent } from '../base-component'
import { $el } from 'src/scripts/utils/utils'
import { fetchConfig } from '../../utils/fetch-config'
import { isEnterKey } from '../../utils/key'

const { routes }: any = window

const SELECTOR_COUNTRIES = '#ShippinCalculator-Countries'
const SELECTOR_PROVINCES = '#ShippinCalculator-Provinces'
const SELECTOR_POSTAL_CODE = '#ShippinCalculator-PostalCode'
const SELECTOR_CALCULATE_BUTTON = '#ShippinCalculator-CalculateButton'
const SELECTOR_FIELD = '.shipping-calculator__field'
const SELECTOR_RESULTS = '.shipping-calculator__results'
const SELECTOR_RESULTS_LIST = '.shipping-calculator__results ul'

const CLASS_LOADING = 'loading'
const CLASS_SUCCESS = 'shipping-calculator--success'
const CLASS_ERRORS = 'shipping-calculator--errors'

const SHIPPING_RATES_ENDPOINT = `${routes.cart_url}/shipping_rates.json`

interface ShippingRate {
  name: string
  price: string
  currency: string
}

interface ShippingErrors {
  [field: string]: string
}

export class ShippingCalculator extends BaseComponent {
  $countries: HTMLSelectElement
  $provinces: HTMLSelectElement
  $postalCode: HTMLInputElement
  $calculateBtn: HTMLElement
  $results: HTMLElement
  $resultsList: HTMLUListElement

  isFirstMount = true

  mountComponent() {
    super.mountComponent()

    this.$countries = $el(SELECTOR_COUNTRIES, this)
    this.$provinces = $el(SELECTOR_PROVINCES, this)
    this.$postalCode = $el(SELECTOR_POSTAL_CODE, this)
    this.$calculateBtn = $el(SELECTOR_CALCULATE_BUTTON, this)
    this.$results = $el(SELECTOR_RESULTS, this)
    this.$resultsList = $el(SELECTOR_RESULTS_LIST, this)

    this.addListener(this.$countries, 'change', this.handleCountryChange)
    this.addListener(this.$calculateBtn, 'click', this.calculate)
    this.addListener(this.$postalCode, 'keyup', this.handlePostalCodeKeyUp)

    if (this.isFirstMount) {
      this.populateProvinceSelector(
        this.getProvinces(this.$countries as HTMLSelectElement),
      )
      this.selectDefaultAddress()
      this.isFirstMount = false
    }
  }

  unmountComponent() {
    super.unmountComponent()

    this.removeListener(this.$countries, 'change', this.handleCountryChange)
    this.removeListener(this.$calculateBtn, 'click', this.calculate)
    this.removeListener(this.$postalCode, 'keyup', this.handlePostalCodeKeyUp)
  }

  handlePostalCodeKeyUp = (event: KeyboardEvent): void => {
    if (isEnterKey(event)) {
      event.preventDefault()
      this.calculate()
    }
  }

  handleCountryChange = (e: MouseEvent) =>
    this.populateProvinceSelector(
      this.getProvinces(e.target as HTMLSelectElement),
    )

  selectDefaultAddress = () => {
    this.selectDefaultCountry()
    this.selectDefaultProvince()
  }

  selectDefaultCountry = () => {
    const defaultCountry = this.$countries.dataset.defaultCountry

    if (defaultCountry) {
      for (const countryOption of this.$countries.options) {
        if (countryOption.textContent === defaultCountry) {
          countryOption.selected = true
          this.$countries.dispatchEvent(new Event('change'))
          break
        }
      }
    }
  }

  selectDefaultProvince = () => {
    const defaultProvince = this.$provinces.dataset.defaultProvince

    if (defaultProvince) {
      for (const provinceOption of this.$provinces.options) {
        if (provinceOption.textContent === defaultProvince) {
          provinceOption.selected = true
          break
        }
      }
    }
  }

  getProvinces = (provincesSelector: HTMLSelectElement) => {
    const { options, selectedIndex } = provincesSelector
    const {
      dataset: { provinces },
    } = options[selectedIndex]

    return JSON.parse(provinces)
  }

  populateProvinceSelector = (provinces: [[]]) => {
    this.$provinces.replaceChildren(...this.createProvinceOptions(provinces))
    this.setHiddenProvinceField(!provinces.length)
  }

  createProvinceOptions = (provinces: [[]]) =>
    provinces.map(province => new Option(...province.reverse()))

  setHiddenProvinceField = (hidden: boolean) =>
    this.$provinces.closest(SELECTOR_FIELD).toggleAttribute('hidden', hidden)

  calculate = async () => {
    this.setButtonLoading(true)

    try {
      const response = await fetch(SHIPPING_RATES_ENDPOINT, {
        ...fetchConfig(),
        body: JSON.stringify({
          shipping_address: {
            zip: this.$postalCode.value,
            country: this.$countries.value,
            province: this.$provinces.value,
          },
        }),
      })

      const json = await response.json()

      if (response.ok) {
        this.showResults(json.shipping_rates)
      } else {
        this.showErrors(json)
      }
    } catch (e) {
      console.error(e)
    } finally {
      this.setButtonLoading(false)
    }
  }

  showResults = (shippingRates: ShippingRate[]) => {
    const results = []

    for (const shippingRate of shippingRates) {
      const li = document.createElement('li')
      li.textContent = `${shippingRate.name}: ${shippingRate.currency} ${shippingRate.price}`
      results.push(li)
    }

    this.$resultsList.replaceChildren(...results)
    this.$results.classList.toggle(CLASS_SUCCESS, true)
    this.$results.classList.toggle(CLASS_ERRORS, false)
    this.$results.toggleAttribute('hidden', false)
  }

  showErrors = (errors: ShippingErrors) => {
    const results = []

    for (const [, message] of Object.entries(errors)) {
      const li = document.createElement('li')
      li.textContent = message
      results.push(li)
    }

    this.$resultsList.replaceChildren(...results)
    this.$results.classList.toggle(CLASS_SUCCESS, false)
    this.$results.classList.toggle(CLASS_ERRORS, true)
    this.$results.toggleAttribute('hidden', false)
  }

  setButtonLoading = (loading: boolean) =>
    this.$calculateBtn.classList.toggle(CLASS_LOADING, loading)
}
